import { registerPlugin, WebPlugin } from '@capacitor/core';

import { appendAppLog } from './appLog';
import { APP_VERSION, geraeteName } from './appVersion';
import { benenneUm } from './dateien';
import { kopiereVideoNachFotos } from './fotos';
import { stelleOrdnerSicher } from './ordner';
import { signFile } from './signing';
import { buildFileName, logFolder, type FileSubject, type LogScope } from './storage';
import { baueVideoWebDeps, nimmAufImBrowser, waehleTyp, type VideoWebDeps } from './videoWeb';

/**
 * Aufnahme von Videokommentaren.
 *
 * Auf dem Gerät steckt dahinter die eigene Kameraoberfläche
 * (`ios/App/App/VideoCaptureViewController.swift`): der Prüfer kann anhalten, das iPad drehen
 * und weiter aufnehmen. Jedes Teilstück wird eine eigene Datei; `VideoSegmentMerger` schneidet
 * sie am Ende zu **einer** zusammen, damit hier nur eine Datei, eine Beglaubigung und ein
 * Protokolleintrag entstehen.
 *
 * **Anders als beim Ton** liefert es den **Pfad** statt des Inhalts: eine Minute Video sind
 * schnell über hundert Megabyte, und als Base64 durch die Brücke geschoben würde das auf einem
 * älteren iPad den Speicher sprengen. Die Datei wird deshalb hier nur noch an ihren Platz
 * verschoben — das Ablageschema bleibt trotzdem an dieser einen Stelle.
 */

/** Wo das Vorschaubild zu einer Videodatei liegt — dieselbe Regel wie in `VideoThumbnail.swift`. */
export const thumbPfad = (videoPfad: string) => videoPfad + '-thumb.jpg';

/**
 * Welchen Augenblick der Aufnahme das Vorschaubild zeigt, in ganzen Millisekunden.
 *
 * ⚠️ **Diese Regel steht an zwei Orten** und muss an beiden dieselbe sein:
 * `ios/App/App/VideoThumbnail.swift::schreibe` zieht den Frame, diese Funktion sagt der
 * Kachel, welche Marken dazu gehören. Weicht eine Seite ab, läge die Markierung neben dem
 * Bild — und dem Standbild sähe niemand an, dass es aus einem anderen Augenblick stammt.
 *
 * ⚠️ **Nicht der erste Frame.** Der Anfang einer Aufnahme ist oft noch verwackelt oder zeigt
 * die Handbewegung zum Auslöser; eine halbe Sekunde später steht das Bild. Nur bei ganz
 * kurzen Aufnahmen gibt es die halbe Sekunde nicht, dann ist es die Mitte.
 */
export function vorschauZeitMs(durationSeconds: number | undefined): number {
  const dauer = durationSeconds ?? 0;
  if (dauer > 1) return 500;
  return Math.round(Math.max(dauer / 2, 0) * 1000);
}

export interface VideoResult {
  /** Absoluter Pfad der fertig zusammengeschnittenen Aufnahme im temporären Ordner. */
  path?: string;
  uri?: string;
  durationSeconds?: number;
  extension?: string;
  /** Aus wie vielen Teilstücken sie entstanden ist — je Pause eines mehr. */
  segments?: number;
  /**
   * Das Vorschaubild, das die native Seite aus der Aufnahme gezogen hat (`VideoThumbnail.swift`).
   * Fehlt, wenn es nicht gelang — dann zeigt die Liste weiter ihr Zeichen.
   */
  thumbPath?: string;
  /** Der Nutzer hat abgebrochen — kein Fehler, sondern der zweithäufigste Ausgang. */
  cancelled?: boolean;
}

export interface VideoRecorderPlugin {
  isAvailable(): Promise<{ available: boolean }>;
  /**
   * `maxSeconds` gilt für die **Summe** aller Teilstücke, nicht je Teilstück.
   *
   * `mitTon` ist die Vorgabe der Einrichtung (`video_audio`), nicht das letzte Wort: die
   * Kameraoberfläche zeigt sie als Startzustand eines Schalters, den der Prüfer vor Beginn
   * noch umlegen kann.
   */
  record(options: { maxSeconds: number; mitTon: boolean }): Promise<VideoResult>;
  addListener(
    ereignis: 'mergeProgress',
    hoerer: (daten: { fraction: number }) => void,
  ): Promise<{ remove: () => Promise<void> }>;
}

/**
 * Videoaufnahme im Browser — `getUserMedia` und `MediaRecorder`.
 *
 * ⚠️ **Bis zum 2026-09-22 warf dieser Rückfall** („Aufnehmen kann er nicht"). Das stimmte
 * nicht: Chrome schreibt `video/mp4;codecs=avc1` unmittelbar, also dasselbe Format wie das
 * Gerät. Was hier steht, ist nur die Verdrahtung; die Arbeit liegt in `platform/videoWeb.ts`.
 */
export class VideoRecorderWeb extends WebPlugin implements VideoRecorderPlugin {
  private readonly deps: ReturnType<typeof baueVideoWebDeps>;
  private readonly kannTyp: (typ: string) => boolean;

  constructor(deps: VideoWebDeps = {}, kannTyp?: (typ: string) => boolean) {
    super();
    this.deps = baueVideoWebDeps(deps, vorschauZeitMs);
    this.kannTyp =
      kannTyp ??
      ((typ) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(typ));
  }

  async isAvailable(): Promise<{ available: boolean }> {
    const hatKamera = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
    return { available: hatKamera && waehleTyp(this.kannTyp) !== null };
  }

  async record(options: { maxSeconds: number; mitTon: boolean }): Promise<VideoResult> {
    const gewaehlt = waehleTyp(this.kannTyp);
    if (!gewaehlt) throw new Error('Dieser Browser kann kein Video aufnehmen.');
    return nimmAufImBrowser(options, this.deps, gewaehlt.endung);
  }

  /*
   * ⚠️ **Es gibt hier nichts zusammenzuschneiden** — `MediaRecorder` schreibt auch über
   * Pausen hinweg **einen** Strom. Der Horcher bleibt trotzdem bestehen: der Aufrufer meldet
   * ihn an und wieder ab (`captureVideo`), und ein fehlendes `addListener` bräche ihn.
   */
  async addListener(
    _ereignis: 'mergeProgress',
    _hoerer: (daten: { fraction: number }) => void,
  ): Promise<{ remove: () => Promise<void> }> {
    return { remove: async () => undefined };
  }
}

export const VideoRecorder = registerPlugin<VideoRecorderPlugin>('VideoRecorder', {
  web: () => new VideoRecorderWeb(),
});

/**
 * Nimmt auf und legt die Datei ab. Gibt den Pfad unterhalb von Documents zurück,
 * oder `null`, wenn abgebrochen wurde.
 */
export async function captureVideo(
  scope: LogScope,
  subject: FileSubject,
  maxSeconds: number,
  mitTon: boolean,
  onMergeProgress?: (anteil: number) => void,
): Promise<{ path: string; durationSeconds: number; segments: number } | null> {
  // Der Zusammenschnitt nach dem Tippen auf „Fertig" dauert spürbar — ohne Rückmeldung hielte
  // der Prüfer die App für hängend und tippte weiter.
  const horcher = onMergeProgress
    ? await VideoRecorder.addListener('mergeProgress', ({ fraction }) => onMergeProgress(fraction))
    : null;

  let result: VideoResult;
  try {
    result = await VideoRecorder.record({ maxSeconds, mitTon });
  } finally {
    await horcher?.remove();
  }
  if (result.cancelled || !result.path) return null;

  const folder = logFolder(scope, 'video');
  await stelleOrdnerSicher(folder);

  const target = folder + '/' + buildFileName('video', subject, result.extension ?? 'mp4');
  /*
   * ⚠️ **`vonAbsolut` ist keine Kleinigkeit.** Nativ liefert die Kamera eine Datei im
   * temporären Ordner des **Systems**, also einen absoluten Pfad. Im Browser gibt es keine
   * absoluten Pfade; dort legt der Rückfall die frische Aufnahme unter `tmp/` in unserer
   * Wurzel ab (`platform/videoWeb.ts::TMP_ORDNER`). Die Fassade unterscheidet das nach Gleis
   * — ohne sie fände das Umbenennen nichts.
   */
  await benenneUm(result.path, target, { vonAbsolut: true });

  // Das Vorschaubild wandert mit und trägt denselben Namen mit `-thumb.jpg` dahinter — der
  // Bezug steckt im Namen, nicht in einer Liste, die von den Dateien abweichen könnte
  // (dieselbe Bauart wie die Markierebene eines Fotos).
  //
  // ⚠️ **Kein Grund zum Abbruch.** Scheitert das Verschieben, fehlt nur das Bild; die
  // Aufnahme liegt dann bereits richtig, und sie ist das, worauf es ankommt.
  if (result.thumbPath) {
    await benenneUm(result.thumbPath, thumbPfad(target), { vonAbsolut: true }).catch(
      () => undefined,
    );
  }

  // Beglaubigen wie jede andere abgelegte Datei. Anders als bei `saveBase64` geschieht es
  // hier von Hand, weil das Video nicht durch den Web-Teil wandert, sondern nur verschoben
  // wird — der Hash entsteht nativ über den Pfad und zieht die Datei nicht durch den
  // Speicher (s. `SigningPlugin.sha256`).
  await signFile(
    target,
    {
      deviceName: geraeteName(),
      examId: subject.examId,
      stationId: subject.stationId ?? '',
      candidateId: subject.candidateId,
      examinerId: subject.examinerId,
      appVersion: APP_VERSION,
    },
    appendAppLog,
  );

  // Zweitschrift in die Fotos-Mediathek, wenn die Einrichtung es eingeschaltet hat — ohne
  // `await`, der Beleg ist geschrieben und beglaubigt. Der Aufruf wirft nicht (s. `fotos.ts`).
  void kopiereVideoNachFotos(target, subject);

  return {
    path: target,
    durationSeconds: Math.round(result.durationSeconds ?? 0),
    segments: result.segments ?? 1,
  };
}
